<script setup>
/**
 * The people found for a user tag (PRIVACY_PLAN.md §3.6, §15), with the one
 * thing to do about each: Follow, Requested, Accept, or the chip that says
 * why not. The box the tag is typed in belongs to whoever uses this (the
 * header search sheet, the profile's Following tab), so both say the same
 * things here. A stranger is found only by their EXACT tag; people the
 * viewer already sees in full (club mates, follows) also by name or the start
 * of their tag. The API decides which; the raw text goes as typed.
 */
import {onBeforeUnmount, ref, watch} from 'vue';
import AvatarImage from '~/components/images/AvatarImage.vue';
import {useFollows} from '~/composables/useFollows';

const props = defineProps({
  /** What has been typed; "@" and case are forgiven. */
  query: {type: String, default: ''},
});
const emit = defineEmits(['followed']);
const {search, request, reasonOf} = useFollows();

const results = ref(null);
const searching = ref(false);
const busyId = ref(null);
const error = ref('');
let timer = null;
let seq = 0;

const run = async () => {
  const q = (props.query || '').trim().replace(/^@/, '');
  if (q.length < 2) {
    results.value = null;
    searching.value = false;
    return;
  }
  const mine = ++seq;
  searching.value = true;
  error.value = '';
  try {
    const found = await search(q) || [];
    if (mine === seq) results.value = found;
  } catch (e) {
    if (mine === seq) error.value = reasonOf(e);
  } finally {
    if (mine === seq) searching.value = false;
  }
};
watch(() => props.query, () => {
  clearTimeout(timer);
  timer = setTimeout(run, 300);
}, {immediate: true});
onBeforeUnmount(() => clearTimeout(timer));

const follow = async (person) => {
  busyId.value = person.playerId;
  error.value = '';
  try {
    await request(person.playerId);
    person.followStatus = 'requested';
    emit('followed', person);
  } catch (e) {
    error.value = reasonOf(e);
  } finally {
    busyId.value = null;
  }
};
const initialsOf = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
</script>

<template>
  <div class="psr">
    <div v-if="searching" class="psr-wait"><v-progress-circular indeterminate size="20" width="2"/></div>
    <template v-else-if="results">
      <div v-if="results.length === 0" class="psr-empty">
        <v-icon size="18">mdi-account-search-outline</v-icon>
        Nobody found. Names only find people in your clubs and people you follow; for anyone else, type their exact user tag. Under-18s and hidden profiles never appear here.
      </div>
      <div v-for="p in results" :key="p.playerId" class="psr-row">
        <avatar-image :size="40" :filename="p.imageFileName" :fallback="initialsOf(p.fullName)"/>
        <div class="psr-who">
          <nuxt-link :to="'/players/' + p.playerId" class="psr-name">{{ p.fullName }} <span class="psr-tag">@{{ p.tag }}</span></nuxt-link>
          <div class="psr-sub">{{ p.clubNames?.length ? p.clubNames.join(' · ') : 'Club not listed' }}</div>
        </div>
        <span v-if="p.followStatus === 'self'" class="tl-chip">That's you</span>
        <span v-else-if="p.followStatus === 'clubMate'" class="tl-chip">In your club</span>
        <span v-else-if="p.followStatus === 'following'" class="tl-chip tl-chip--ok">Following</span>
        <v-btn v-else-if="p.followStatus === 'requested'" size="small" variant="outlined" rounded="pill" disabled>Requested</v-btn>
        <v-btn v-else-if="p.followStatus === 'requestReceived'" size="small" color="primary" rounded="pill" :to="'/players/' + p.playerId">Respond</v-btn>
        <v-btn v-else-if="p.followStatus === 'none'" size="small" color="primary" rounded="pill" prepend-icon="mdi-account-plus"
               :loading="busyId === p.playerId" @click="follow(p)">Follow</v-btn>
      </div>
    </template>
    <div v-if="error" class="psr-error">{{ error }}</div>
  </div>
</template>

<style scoped>
.psr-wait { padding: 14px 0; text-align: center; }
.psr-empty { display: flex; align-items: center; gap: 8px; padding: 10px 2px 4px; color: rgb(var(--v-theme-textSubtle)); font-size: 13px; }
.psr-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); }
.psr-row:first-of-type { border-top: 0; }
.psr-who { flex: 1 1 auto; min-width: 0; }
.psr-name { font-size: 14px; font-weight: 700; color: rgb(var(--v-theme-on-surface)); text-decoration: none; }
.psr-name:hover { text-decoration: underline; }
.psr-tag { font-weight: 500; color: rgba(0, 0, 0, 0.55); margin-left: 4px; font-size: 13px; }
.psr-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 1px; }
.psr-error { font-size: 12.5px; color: rgb(var(--v-theme-error)); margin-top: 6px; }
</style>
